import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ResponseDialogComponent } from '@app/@shared/dialogs/response/response-dialog.component';
import { AgentDTO } from '@app/requests/model/requests-model';
import { AuthenticationService, CredentialsService } from '@app/login';
import { Credentials } from '@app/@core/models/login-models';
import { CombosStore } from '@app/requests/combos-service/combos.store';
import { CommonDataDTO } from '@app/requests/model/combos-model';
import { RevolvingStore } from '../services/revolving.store';
import { CustomValidatorsService } from '@app/@shared/custom-validators/custom-validators.service';
import { PrintStore } from '@app/@shared';
import { TPVTariff } from '../model/revolving-model';
import { UserAttributionsEnum } from '@app/@core/constants/user-credentials.constants';
import { MAX_SELECTOR_DATA } from '@app/@core/constants/material-custom.constants';

@Component({
  selector: 'app-purchases',
  templateUrl: './purchases.component.html',
  styleUrls: ['./purchases.component.scss'],
})
export class PurchasesComponent implements OnInit, OnDestroy {
  public purchasesDataForm: FormGroup;
  public fractionalOperationForm: FormGroup;
  public chkFractionalOp: boolean = false;
  public isBtnEnabled: boolean = false;

  private agentDTOUser: AgentDTO;
  private userInfo: Credentials;

  public establishmentsList: CommonDataDTO[] = [];
  public monthlyPaymentsList: CommonDataDTO[] = [];
  public tariffCodeList: TPVTariff[] = [];

  public hasAttributionInstallmentsPayment: boolean;
  public hasAttributionRegularPayment: boolean;

  constructor(
    private formBuilder: FormBuilder,
    public translate: TranslateService,
    public dialog: MatDialog,
    private credentials: CredentialsService,
    private combosStore: CombosStore,
    private revolvingStore: RevolvingStore,
    private customValidatorsService: CustomValidatorsService,
    private printStore: PrintStore,
    private authenticationService: AuthenticationService
  ) {
    this.userInfo = this.credentials.credentials;
    this.agentDTOUser = {
      division: this.userInfo.division,
      typeUser: this.userInfo.userType === 'V' ? this.userInfo.parentType : this.userInfo.userType,
      userId: this.userInfo.userType === 'V' ? this.userInfo.parentId : this.userInfo.userId,
    };
  }

  ngOnDestroy(): void {}

  ngOnInit(): void {
    this.purchasesDataForm = this.formBuilder.group({
      establishment: ['', Validators.required],
      contractNum: ['', Validators.required],
      nifNie: ['', [Validators.required, this.customValidatorsService.isNifNieValid]],
      purchaseImport: ['', Validators.required],
    });

    this.fractionalOperationForm = this.formBuilder.group({
      tariffCode: ['', Validators.required],
      monthlyPayments: ['', Validators.required],
      description: [''],
    });

    this.hasAttributionInstallmentsPayment = this.authenticationService.permissionAction(
      UserAttributionsEnum.ATTR_REVOLVING_INSTALLMENTS_PAYMENT
    );
    this.hasAttributionRegularPayment = this.authenticationService.permissionAction(
      UserAttributionsEnum.ATTR_REVOLVING_REGULAR_PAYMENT
    );

    if (!this.hasAttributionRegularPayment && this.hasAttributionInstallmentsPayment) this.chkFractionalOp = true;

    this.combosStore.getEstablishments().then(
      (data) => {
        this.establishmentsList = data;
        this.checkEstablishment();
      },
      (error) => console.error(error)
    );

    this.purchasesDataForm.controls.establishment.valueChanges.subscribe((value) => {
      if (value) {
        this.tariffCodeList = [];
        this.monthlyPaymentsList = [];
        this.revolvingStore.loadTariffs(value).then(
          (data) => {
            if (data) {
              data.forEach((tariff, index) => {
                this.tariffCodeList.push({ id: tariff.code, value: tariff.description, terms: [] });
                tariff.terms.forEach((term) => {
                  this.tariffCodeList[index].terms.push({ id: term.value, value: term.value });
                });
              });
              this.checkTariff();
            }
          },
          (error) => console.error(error)
        );
      }
    });

    this.fractionalOperationForm.controls.tariffCode.valueChanges.subscribe((value) => {
      if (this.chkFractionalOp) {
        let index = this.tariffCodeList.findIndex((tariff) => tariff.id === value);
        this.monthlyPaymentsList = this.tariffCodeList[index]?.terms;
        this.checkTerms();
      }
    });

    this.purchasesDataForm.statusChanges.subscribe(() => {
      this.chkFractionalOp
        ? (this.isBtnEnabled = this.purchasesDataForm.valid && this.fractionalOperationForm.valid)
        : (this.isBtnEnabled = this.purchasesDataForm.valid);
    });

    this.fractionalOperationForm.statusChanges.subscribe(() => {
      if (this.chkFractionalOp) {
        this.isBtnEnabled = this.purchasesDataForm.valid && this.fractionalOperationForm.valid;
      }
    });
  }

  isFractionalOperation() {
    if (this.chkFractionalOp) {
      this.isBtnEnabled = this.purchasesDataForm.valid && this.fractionalOperationForm.valid;
    } else {
      this.fractionalOperationForm.reset();
      this.isBtnEnabled = this.purchasesDataForm.valid;
    }
  }

  cancelClick() {
    this.purchasesDataForm.reset();
    this.fractionalOperationForm.reset();
    this.checkNumValues();
  }

  acceptClick() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.maxWidth = '500px';

    this.revolvingStore
      .sendPurchase(this.purchasesDataForm, this.fractionalOperationForm)
      .then(
        (data) => {
          dialogConfig.data = {
            title: this.translate.instant('purchases.dialog.authorizedOperation.title'),
            text: this.translate.instant('purchases.dialog.authorizedOperation.text'),
            icon: 'icon-ic_circle_check',
          };
          const dialog = this.dialog.open(ResponseDialogComponent, dialogConfig);
          dialog
            .afterClosed()
            .toPromise()
            .then(
              () => {
                this.printStore.downloadTpvPurchasePrint(data.datosBoletaBean);
              },
              (error) => console.error(error)
            );
        },
        (error) => {
          console.error(error);
          let errorMsg = error.error?.result
            ? error.error?.result
            : this.translate.instant('purchases.dialog.rejectedOperation.text');
          dialogConfig.data = {
            title: this.translate.instant('purchases.dialog.rejectedOperation.title'),
            text: errorMsg,
            icon: 'icon-ic_circle_cross',
          };
          this.dialog.open(ResponseDialogComponent, dialogConfig);
        }
      )
      .finally(() => {
        this.purchasesDataForm.reset();
        this.fractionalOperationForm.reset();
        this.checkNumValues();
      });
  }

  numberOnly(event: any, element: any): boolean {
    const inputForm = typeof element === 'string' ? element : element?.getAttribute('formControlName');
    let value = this.purchasesDataForm.controls[inputForm].value;

    return this.customValidatorsService.numberOnly(event.which ? event.which : event.keyCode, value);
  }

  transformAmount(value: any, element: any) {
    const inputForm = typeof element === 'string' ? element : element?.getAttribute('formControlName');
    let inputValue = typeof value === 'string' ? value : value?.target.value;

    this.purchasesDataForm.controls[inputForm].setValue(this.customValidatorsService.transformAmount(inputValue));
  }

  checkNumValues() {
    this.checkEstablishment();
    if (this.chkFractionalOp) {
      this.checkTariff();
      this.checkTerms();
    }
  }

  checkEstablishment() {
    if (this.establishmentsList.length === 1) {
      this.purchasesDataForm.controls.establishment.setValue(this.establishmentsList[0].id);
    }
  }

  checkTariff() {
    if (this.tariffCodeList.length === 1) {
      this.fractionalOperationForm.controls.tariffCode.setValue(this.tariffCodeList[0]?.id);
    }
  }

  checkTerms() {
    if (this.monthlyPaymentsList?.length === 1) {
      this.fractionalOperationForm.controls.monthlyPayments.setValue(this.monthlyPaymentsList[0]?.id);
    }
  }

  isGenericSelect() {
    return this.establishmentsList?.length < MAX_SELECTOR_DATA;
  }
}
